import { Link } from 'react-router-dom';
import logoImage from '@/assets/logo-small.webp';
import { cn } from '@/lib/utils';
import { OptimizedImage } from '@/components/OptimizedImage';

interface LogoProps {
  to?: string;
  showText?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
  /** Use lazy loading (for footer/below-fold usage) */
  lazy?: boolean;
}

const sizeClasses = {
  sm: 'w-7 h-7',
  md: 'w-8 h-8',
  lg: 'w-10 h-10',
};

const sizePixels = {
  sm: 28,
  md: 32,
  lg: 40,
};

const textSizeClasses = {
  sm: 'text-base',
  md: 'text-lg',
  lg: 'text-xl',
};

export function Logo({ 
  to = '/', 
  showText = true, 
  size = 'md',
  className,
  onClick,
  lazy = false,
}: LogoProps) {
  const px = sizePixels[size];
  const content = (
    <>
      <OptimizedImage 
        src={logoImage} 
        alt="ElevAdvisor" 
        width={px}
        height={px}
        className="w-full h-full object-cover"
        containerClassName={cn(
          'rounded-lg flex-shrink-0',
          sizeClasses[size]
        )}
        lazy={lazy}
        blurAmount={8}
      />
      {showText && (
        <span className={cn(
          'font-semibold text-foreground',
          textSizeClasses[size]
        )}>
          ElevAdvisor
        </span>
      )}
    </>
  );

  if (to) {
    return (
      <Link 
        to={to} 
        className={cn(
          'flex items-center gap-2 flex-shrink-0 touch-manipulation active:opacity-70 transition-opacity',
          className
        )}
        onClick={onClick}
      >
        {content}
      </Link>
    );
  }

  return (
    <div className={cn('flex items-center gap-2 flex-shrink-0', className)}>
      {content}
    </div>
  );
}

// Standalone logo icon without link
export function LogoIcon({ size = 'md', className, lazy = false }: { size?: 'sm' | 'md' | 'lg'; className?: string; lazy?: boolean }) {
  const px = sizePixels[size];
  return (
    <OptimizedImage 
      src={logoImage} 
      alt="ElevAdvisor" 
      width={px}
      height={px}
      className="w-full h-full object-cover"
      containerClassName={cn(
        'rounded-lg',
        sizeClasses[size],
        className
      )}
      lazy={lazy}
      blurAmount={8}
    />
  );
}
